// Task 4 (team-hierarchy-t4) — supervisor delegation dialog for a team.
// Modal structure mirrors TeamDialog.tsx (same styles.modalBackdrop/modal/
// field/actions classes, window-level Escape useEffect per the T2 fix,
// autoFocus on the first interactive control). Two sections: (a) a list of
// active + upcoming delegations with a Revoke action, (b) a create form for
// a new one.
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "@/lib/api-client";
import { toast } from "@/components/Toaster";
import { getApiErrorCode, showApiError } from "@/lib/error-toast";
import { formatTimestamp } from "@/lib/formatters";
import { teamsApi, type TeamRecord, type TenantUser, type DelegationRecord } from "../api/client";
import styles from "../teams.module.css";

const REASON_MAX = 200;

export interface DelegationDialogProps {
  team: TeamRecord;
  /** Full team list — used to compute the leads-first sort in the covering-sup picker. */
  teams: TeamRecord[];
  /** Tenant users — covering-sup picker source. */
  users: TenantUser[];
  onClose: () => void;
  /** Called after a successful create/revoke so the parent can refetch teams (badge count changes). */
  onChanged: () => void;
}

export function DelegationDialog({ team, teams, users, onClose, onChanged }: DelegationDialogProps) {
  const [delegations, setDelegations] = useState<DelegationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [toUserId, setToUserId] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [reason, setReason] = useState("");
  const [windowError, setWindowError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await teamsApi.listDelegations(team.id);
      setDelegations(res.delegations);
    } catch (e) {
      showApiError(e);
    } finally {
      setLoading(false);
    }
  }, [team.id]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  // Task 11/T2 fix — window-level listener (FieldCreateModal idiom), not a
  // div-level onKeyDown, so Escape closes even after focus lands on <body>.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // §7.2 "supervisors sorted first" — approximated as team leads since
  // roles aren't client-visible here; no hard-block on picking a non-lead
  // per spec §4 (the server doesn't require the covering user to be a lead).
  const sortedUsers = useMemo(() => {
    const leadIds = new Set(teams.map((t) => t.supervisorUserId));
    return [...users]
      .filter((u) => u.isActive)
      .sort((a, b) => {
        const rank = (leadIds.has(b.id) ? 1 : 0) - (leadIds.has(a.id) ? 1 : 0);
        if (rank !== 0) return rank;
        return a.name.localeCompare(b.name);
      });
  }, [teams, users]);

  const trimmedReason = reason.trim();
  const canSave =
    toUserId !== "" &&
    startsAt !== "" &&
    endsAt !== "" &&
    trimmedReason.length > 0 &&
    trimmedReason.length <= REASON_MAX &&
    new Date(endsAt) > new Date(startsAt) &&
    !busy;

  async function handleRevoke(delegation: DelegationRecord) {
    if (!window.confirm(`Revoke the delegation to ${delegation.toUserName ?? delegation.toUserId}?`)) return;
    setBusy(true);
    try {
      await teamsApi.revokeDelegation(delegation.id);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && getApiErrorCode(e) === "already_revoked") {
        toast.error("Already revoked");
      } else {
        showApiError(e);
      }
    } finally {
      setBusy(false);
      await refetch();
      onChanged();
    }
  }

  async function handleCreate() {
    setBusy(true);
    setWindowError(null);
    try {
      await teamsApi.createDelegation(team.id, {
        toUserId,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        reason: trimmedReason,
      });
      setToUserId("");
      setStartsAt("");
      setEndsAt("");
      setReason("");
      await refetch();
      onChanged();
    } catch (e) {
      if (e instanceof ApiError && e.status === 400 && getApiErrorCode(e) === "INVALID_WINDOW") {
        setWindowError("End must be after start");
        return;
      }
      showApiError(e);
    } finally {
      setBusy(false);
    }
  }

  // Shared onChange for the two window inputs — both clear the inline
  // window error on edit (/simplify — the handlers were copy-pasted).
  const handleWindowChange =
    (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value);
      if (windowError) setWindowError(null);
    };

  const now = Date.now();

  return (
    <div className={styles.modalBackdrop} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        <h2>Delegations — {team.name}</h2>

        {loading ? (
          <p className={styles.help}>Loading…</p>
        ) : delegations.length === 0 ? (
          <p className={styles.help}>No active or upcoming delegations.</p>
        ) : (
          <ul className={styles.memberList}>
            {delegations.map((d) => {
              const isActive = Date.parse(d.startsAt) <= now;
              return (
                <li key={d.id}>
                  <div>
                    <span>{d.toUserName ?? d.toUserId}</span>{" "}
                    <span className={styles.archivedBadge}>{isActive ? "Active" : "Upcoming"}</span>
                    <div className={styles.help}>
                      {formatTimestamp(d.startsAt)} – {formatTimestamp(d.endsAt)}
                    </div>
                    <div className={styles.help}>{d.reason}</div>
                  </div>
                  <button type="button" disabled={busy} onClick={() => handleRevoke(d)}>
                    Revoke
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className={styles.field}>
          <label htmlFor="delegation-to-user">
            <span>Covering supervisor</span>
          </label>
          <select
            id="delegation-to-user"
            value={toUserId}
            aria-label="covering supervisor"
            onChange={(e) => setToUserId(e.target.value)}
            autoFocus
          >
            <option value="">— select a user —</option>
            {sortedUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label htmlFor="delegation-starts">
            <span>Starts</span>
          </label>
          <input
            id="delegation-starts"
            type="datetime-local"
            value={startsAt}
            aria-label="starts"
            onChange={handleWindowChange(setStartsAt)}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="delegation-ends">
            <span>Ends</span>
          </label>
          <input
            id="delegation-ends"
            type="datetime-local"
            value={endsAt}
            aria-label="ends"
            onChange={handleWindowChange(setEndsAt)}
          />
          {windowError && <span className={styles.fieldError}>{windowError}</span>}
        </div>

        <div className={styles.field}>
          <label htmlFor="delegation-reason">
            <span>Reason</span>
          </label>
          <input
            id="delegation-reason"
            type="text"
            value={reason}
            maxLength={REASON_MAX}
            aria-label="reason"
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Covering vacation"
          />
          <small className={styles.help}>
            {reason.length} / {REASON_MAX}
          </small>
        </div>

        <div className={styles.actions}>
          <button type="button" onClick={onClose}>
            Close
          </button>
          <button type="button" className={styles.primary} disabled={!canSave} onClick={handleCreate}>
            Create delegation
          </button>
        </div>
      </div>
    </div>
  );
}
